import React, {useCallback, useEffect, useState} from 'react'

import {FindUserArgs, LoginArgs} from 'airship/clients/auth/types'
import getUrl from 'airship/modules/get-url'
import execute, {
  isLoaded as isRecaptchaLoaded,
} from 'airship/modules/grecaptcha'
import pageContext from 'airship/modules/page-context'
import Button from 'airship/patterns/button'
import Checkbox from 'airship/patterns/checkbox'
import Feedback from 'airship/patterns/feedback'
import {LabeledTextInput} from 'airship/patterns/TextInput'

import AuthAnchorLink from '../components/AuthAnchorLink'
import loginApi from './api'
import {DEFAULT_LOGIN_ERROR_MESSAGE} from './constants'
import styles from './styles/TwoPartLoginForm.module.css'
import {resolveLoginPageContext} from './types'
import {getLoginErrorMessage, getQueryStringParams, redirect} from './utils'

interface Props {
  recaptchaLoaded: boolean
}

const TwoPartLoginForm: React.FC<Props> = ({recaptchaLoaded}) => {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [staySignedIn, setStaySignedIn] = useState(true)

  const [
    login,
    {data: loginData, error: loginError, isLoading: isLoginLoading},
  ] = loginApi.useLoginMutation()

  const [
    findUser,
    {data: findUserData, error: findUserError, isLoading: isFindUserLoading},
  ] = loginApi.useFindUserMutation()

  const loading = isLoginLoading || isFindUserLoading
  const redirectTo = loginData?.redirectTo || findUserData?.redirectTo
  // Display login whether the user is found or not to prevent discovery
  const displayLogin = findUserData?.displayLogin || findUserError

  const {requireCaptcha, recaptchaSiteKey} = pageContext(
    resolveLoginPageContext
  )

  const waitingForRecaptcha = requireCaptcha && !recaptchaLoaded

  const continueCallback = useCallback(async () => {
    const data: FindUserArgs = {
      username,
      queryStringParams: getQueryStringParams(),
    }

    if (requireCaptcha) {
      if (!isRecaptchaLoaded()) return undefined
      const token = await execute(recaptchaSiteKey)
      data.recaptchaToken = token
    }

    await findUser(data)
  }, [findUser, username, requireCaptcha, recaptchaSiteKey])

  const loginCallback = useCallback(async () => {
    const data: LoginArgs = {
      username,
      password,
      staySignedIn,
      queryStringParams: getQueryStringParams(),
    }

    if (requireCaptcha) {
      if (!isRecaptchaLoaded()) return undefined
      const token = await execute(recaptchaSiteKey)
      data.recaptchaToken = token
    }

    await login(data)
  }, [
    login,
    username,
    password,
    staySignedIn,
    requireCaptcha,
    recaptchaSiteKey,
  ])

  const preventFormSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    // Prevent page load on form submit
    event.preventDefault()
  }

  useEffect(() => {
    // Redirect on: login success, failures that initiate a redirect, or Okta migrated users
    if (redirectTo) redirect(redirectTo)
  }, [redirectTo])

  return (
    <form onSubmit={preventFormSubmit}>
      <fieldset>
        <div>
          <LabeledTextInput
            id={'username'}
            label={'Username'}
            placeholder="Enter username (usually an email address)"
            onChange={setUsername}
            value={username}
            readOnly={!!displayLogin}
            inputDisplayClasses={!!displayLogin ? styles.textInputDisabled : ''}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={!displayLogin}
          />
          {displayLogin && (
            <AuthAnchorLink
              url={window.location.href}
              display="Log in with a different user"
            />
          )}
        </div>
        {displayLogin && (
          <div>
            <LabeledTextInput
              id={'password'}
              label={'Password'}
              placeholder="Enter your password"
              onChange={setPassword}
              value={password}
              password={true}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={!!displayLogin}
            />
            <AuthAnchorLink
              url={getUrl('auth_page:account_recovery')}
              display={'Forgot your password?'}
            />
          </div>
        )}
        {loginError && (
          <Feedback type="error">
            {'data' in loginError
              ? getLoginErrorMessage(loginError)
              : DEFAULT_LOGIN_ERROR_MESSAGE}
          </Feedback>
        )}
        {displayLogin && (
          <div>
            <Checkbox
              checked={staySignedIn}
              onChange={setStaySignedIn}
              disabled={loading}
            >
              Stay logged in for 2 weeks
            </Checkbox>
          </div>
        )}
        {!displayLogin && (
          <div>
            <Button
              type="submit"
              displayClasses={styles.button}
              disabled={
                loading || waitingForRecaptcha || !username || !!redirectTo
              }
              style="blue"
              onClick={continueCallback}
            >
              Continue
            </Button>
          </div>
        )}
        {displayLogin && (
          <div>
            <Button
              type="submit"
              displayClasses={styles.button}
              disabled={
                loading ||
                waitingForRecaptcha ||
                !username ||
                !password ||
                !!redirectTo
              }
              style="blue"
              onClick={loginCallback}
            >
              {loading || !!redirectTo ? 'Logging in...' : 'Log in'}
            </Button>
          </div>
        )}
      </fieldset>
    </form>
  )
}

export default TwoPartLoginForm
